// Maps a completed order → Intercom custom attributes. See ../README.md § Intercom attribute
// contract for the validate-missing rule: emit every key, '' when unknown, never a fake value.
import { CompletedOrder } from 'src/public/components/online_ordering/types'
import {
  IntercomCustomAttributeInput,
  OrderSource
} from 'src/apollo/onlineOrdering'
import { IntercomData } from './types'
import type { ExceptionKind } from './reasons'
import { refundTotalAmount } from './refundItemPricing'

// What iOS signs for 'Order source' (IntercomInteracting's supportAttributeValue). Every other
// FormattableOrderSource case signs its raw SCREAMING_SNAKE value, identical to web's OrderSource,
// so only these two translate. Web only reaches LOCAL today; CONSUMER_APP is here so it can't drift.
const IOS_ORDER_SOURCE: Partial<Record<OrderSource, string>> = {
  [OrderSource.Local]: 'local',
  [OrderSource.ConsumerApp]: 'consumerApp'
}

// Matches iOS byte-for-byte: agents filter on this across both platforms, so 'LOCAL' and 'local'
// must not both end up in the workspace.
export function intercomOrderSource(
  source: CompletedOrder['source'] | null | undefined
): string {
  if (!source) return ''
  return IOS_ORDER_SOURCE[source] ?? source
}

export function buildIntercomData(
  order?: CompletedOrder | null
): IntercomData | null {
  if (!order?.guid) return null
  const location = order.restaurant?.location
  return {
    'Restaurant GUID': order.restaurant?.guid ?? '',
    'Restaurant Name': order.restaurant?.name ?? '',
    'Restaurant Phone': location?.phone ?? '',
    'Restaurant Address': location?.address1 ?? '',
    'Restaurant City': location?.city ?? '',
    'Restaurant State': location?.state ?? '',
    'Restaurant Zip': location?.zip ?? '',
    'Order GUID': order.guid,
    'Check Number': order.checkNumber ?? '',
    'Order State': order.approvalStatus ?? '',
    'Order Total': order.totalV2 ?? '',
    'Order Date':
      order.createdDateTime != null
        ? new Date(order.createdDateTime).toISOString()
        : '',
    'Order source': intercomOrderSource(order.source),
    'Fulfillment Type': order.diningOptionBehavior,
    'Delivery ID': order.deliveryServiceInfo?.providerId ?? '',
    'Is TDS order': Boolean(order.deliveryServiceInfo),
    'DPS Provider': order.deliveryServiceInfo?.providerName ?? '',
    // The five delivery keys iOS signs, same camelCase names — see ../README.md § iOS attribute
    // parity (incl. the retention caveat). Verbatim: the `stringified*` pair is already formatted
    // and capped by the service. `?? ''` covers non-delivery orders and absent photo/tracking.
    deliveryStatusHistory: order.stringifiedDeliveryStatusHistory ?? '',
    redispatchDeliveryStatusHistory:
      order.stringifiedRedispatchDeliveryStatusHistory ?? '',
    deliveryPhoto: order.deliveryDropOffPhotoUrl ?? '',
    trackingUrl: order.deliveryTrackingUrl ?? '',
    originalQuotedDeliveryTime:
      order.deliveryServiceInfo?.originalQuotedDeliveryDate ?? ''
  }
}

// Must match the workflow audience predicates EXACTLY (ruleset 67364391 / 67630938). Mirrors iOS
// IntercomSupportContext.RefundContext.Method; `unknown` has no web equivalent because D3 renders
// that case itself (see useInitializeRefund.ts § UNQUOTABLE). Only the first two are guest choices:
// the exceptions and the two fixed manual-review values deliberately match neither refund audience,
// which is what keeps an unrefundable claim out of the refund workflow. The exceptions ride on this
// attribute rather than 'Order support selection' (that one keeps the guest's D1 reason), as on iOS.
// The manual-review literals must stay in sync with reasons.ts's FixedRefundMethod; tests pin both.
export const RefundMethod = {
  ToastCash: 'Toast Cash',
  OriginalPaymentMethod: 'Original payment method',
  OrderAlreadyRefunded: 'Order already refunded',
  OutsideClaimWindow: 'Outside claim window',
  ManualReview: 'Manual review',
  Other: 'Other'
} as const

// Spelled out rather than derived from the whole object, so widening RefundMethod with values
// nobody picks cannot quietly let one reach the refund launch path.
export type GuestRefundMethod =
  | typeof RefundMethod.ToastCash
  | typeof RefundMethod.OriginalPaymentMethod

// The fixed methods a manual-review leaf hands off with. Mirrors reasons.ts's FixedRefundMethod.
export type ManualReviewMethod =
  | typeof RefundMethod.ManualReview
  | typeof RefundMethod.Other

// Which methods pay the bonus on top of the base, mirroring iOS's RefundContext.Method.includesBonus.
// `Other` being in and `Manual review` out is iOS's split, not web routing — reasons.ts sends both to
// a human and quotes both. The exception values never reach here: nothing is reserved on those paths,
// so the card passes no amounts and the guard below returns '' first.
const BONUS_PAYING_METHODS: readonly string[] = [
  RefundMethod.ToastCash,
  RefundMethod.Other
]

// The single figure iOS signs as 'Estimated refund': what the SELECTED method would actually pay
// out — base alone, or base + bonus — so an agent doesn't have to know which methods carry the
// bonus. iOS signs all three amounts, so this does not replace 'Base refund amount' / 'Bonus refund
// amount'. '' whenever there is nothing to resolve (no method picked, or no reserved quote — an
// exception path, or one the service declined); iOS sends 'N/A' there and web's '' convention wins.
// See ../README.md § Refund amount.
export function intercomEstimatedRefund(
  method: string | undefined,
  baseRefundAmount: string | undefined,
  bonusRefundAmount: string | undefined
): string {
  if (!method || !baseRefundAmount) return ''
  // Verbatim, as 'Base refund amount' passes it: for a base-only method the two attributes describe
  // the same reserved figure and must not disagree by a rounding step.
  if (!BONUS_PAYING_METHODS.includes(method)) return baseRefundAmount
  // toFixed(2) matches iOS's twoDecimalPlaceDescription ("%.2f"), and refundTotalAmount owns the
  // cents rounding that adding two decimal strings as floats would lose.
  return refundTotalAmount({
    baseAmount: { amount: baseRefundAmount },
    bonusAmount: { amount: bonusRefundAmount ?? '0' }
  }).toFixed(2)
}

// Mirrors the `refundMethod` iOS hangs off each `OrderSupportException` case, in one place so the
// card cannot pair a screen with the wrong value.
export const EXCEPTION_REFUND_METHOD: Record<ExceptionKind, string> = {
  alreadyReported: RefundMethod.OrderAlreadyRefunded,
  outsideClaimWindow: RefundMethod.OutsideClaimWindow
}

export interface RefundFlowSelections {
  sourceChannel?: string
  selectedRefundMethod?: string
  orderSupportSelection?: string
  // GuestCurrencyRefund.id — the reservation the agent finalizes.
  refundGuid?: string
  // The service's own decimal strings, verbatim: Number('39.70') → '39.7' would show an agent a
  // different figure than the one reserved.
  baseRefundAmount?: string
  bonusRefundAmount?: string
  // The D2 items — the same CompletedOrderSelection guids initializeRefund reserves via
  // `selectionIds`. Duplicated here because an agent reads the conversation, not the reservation.
  selectedItemGuids?: string[]
}

// Order data + recovery selections — the payload signed into the guest JWT.
export function buildRefundIntercomAttributes(
  order: CompletedOrder | null | undefined,
  selections: RefundFlowSelections = {}
): IntercomData | null {
  const data = buildIntercomData(order)
  if (!data) return null
  return {
    ...data,
    'Source channel': selections.sourceChannel ?? 'Refund support',
    // Never defaulted: both are matched EXACTLY by the audience rulesets, so an absent value must
    // sign no claim rather than a wrong one. Defaulting the method to Toast Cash meant a caller
    // that booted before the guest picked filed their refund as Toast Cash.
    'Selected refund method': selections.selectedRefundMethod,
    'Order support selection': selections.orderSupportSelection,
    'Refund GUID': selections.refundGuid ?? '',
    // Derived from the three above rather than plumbed through RefundFlowSelections: a fourth field
    // the card had to keep consistent with them is a fourth field it could get wrong.
    'Estimated refund': intercomEstimatedRefund(
      selections.selectedRefundMethod,
      selections.baseRefundAmount,
      selections.bonusRefundAmount
    ),
    'Base refund amount': selections.baseRefundAmount ?? '',
    'Bonus refund amount': selections.bonusRefundAmount ?? '',
    // Comma-separated: Intercom custom attributes are scalars (see toIntercomCustomAttributes).
    'Selected item GUIDs': selections.selectedItemGuids?.join(', ') ?? ''
  }
}

export function toIntercomCustomAttributes(
  data: IntercomData
): IntercomCustomAttributeInput[] {
  return Object.entries(data)
    .filter(([, value]) => value !== undefined && value !== null)
    .map(([key, value]) => ({ key, value: String(value) }))
}
